import {
	type ReactNode,
	createContext,
	useContext,
	useMemo,
	useState,
} from "react";

interface NavPendingRenameContextType {
	pendingRenameItemId: string | null;
	requestRename: (itemId: string) => void;
	consumeRename: (itemId: string) => void;
}

const NavPendingRenameContext = createContext<
	NavPendingRenameContextType | undefined
>(undefined);

NavPendingRenameContext.displayName = "NavPendingRenameContext";

export function NavPendingRenameProvider({
	children,
}: {
	children: ReactNode;
}) {
	const [pendingRenameItemId, setPendingRenameItemId] = useState<string | null>(
		null,
	);

	const value = useMemo(
		() => ({
			pendingRenameItemId,
			requestRename: (itemId: string) => setPendingRenameItemId(itemId),
			consumeRename: (itemId: string) =>
				setPendingRenameItemId((current) =>
					current === itemId ? null : current,
				),
		}),
		[pendingRenameItemId],
	);

	return (
		<NavPendingRenameContext.Provider value={value}>
			{children}
		</NavPendingRenameContext.Provider>
	);
}

export function useNavPendingRename() {
	const context = useContext(NavPendingRenameContext);
	if (!context) {
		return {
			pendingRenameItemId: null,
			requestRename: () => {},
			consumeRename: () => {},
		};
	}
	return context;
}
